import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { ArrowBendDownRight, PencilSimple, Trash, Flag } from '@phosphor-icons/react';

import { ConfirmDialog } from '@/components/ui';
import { UserAvatar } from '@/components/features/UserAvatar/UserAvatar';
import { CommentInput } from '@/components/features/CommentInput/CommentInput';
import { ReportDialog } from '@/components/features/ReportDialog/ReportDialog';
import { useAuthStore } from '@/stores';

import type { Comment } from '@/types/poll';

import styles from './CommentItem.module.css';

interface CommentItemProps {
    comment: Comment;
    onReply: (text: string, parentId: number) => void;
    onEdit: (commentId: number, text: string) => void;
    onDelete: (commentId: number) => void;
    depth?: number;
}

export function CommentItem({
    comment,
    onReply,
    onEdit,
    onDelete,
    depth = 0,
}: CommentItemProps) {
    const [showReply, setShowReply] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState(comment.text);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showReportDialog, setShowReportDialog] = useState(false);
    const { isLogged, getUser } = useAuthStore();
    const user = getUser();
    const isOwner = user?.id === comment.user.id;

    const timeAgo = formatDistanceToNow(new Date(comment.created), {
        addSuffix: true,
        locale: es,
    });

    const handleReply = (text: string) => {
        onReply(text, comment.id);
        setShowReply(false);
    };

    const handleEdit = (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = editText.trim();
        if (!trimmed) return;
        onEdit(comment.id, trimmed);
        setIsEditing(false);
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const confirmDelete = () => {
        setShowDeleteConfirm(false);
        onDelete(comment.id);
    };

    return (
        <div className={`${styles.container} ${depth > 0 ? styles.nested : ''}`}>
            <ConfirmDialog
                isOpen={showDeleteConfirm}
                title="Eliminar comentario"
                message="Estas seguro que quieres eliminar este comentario?"
                confirmLabel="Eliminar"
                cancelLabel="Cancelar"
                onConfirm={confirmDelete}
                onCancel={() => setShowDeleteConfirm(false)}
                variant="danger"
            />

            {isLogged && !isOwner && (
                <ReportDialog
                    isOpen={showReportDialog}
                    contentType="comment"
                    contentId={comment.id}
                    onClose={() => setShowReportDialog(false)}
                />
            )}

            <div className={styles.comment}>
                <UserAvatar
                    src={comment.user.avatar_url}
                    name={comment.user.display_name}
                    size="sm"
                />
                <div className={styles.body}>
                    <div className={styles.header}>
                        <span className={styles.name}>{comment.user.display_name}</span>
                        <span className={styles.time}>{timeAgo}</span>
                        {comment.is_edited && (
                            <span className={styles.edited}>(editado)</span>
                        )}
                    </div>
                    {isEditing ? (
                        <form onSubmit={handleEdit} className={styles.editForm}>
                            <input
                                className={styles.editInput}
                                value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                                autoFocus
                                maxLength={2000}
                            />
                            <div className={styles.editActions}>
                                <button type="submit" className={styles.actionBtn}>
                                    Guardar
                                </button>
                                <button
                                    type="button"
                                    className={styles.actionBtn}
                                    onClick={() => {
                                        setIsEditing(false);
                                        setEditText(comment.text);
                                    }}
                                >
                                    Cancelar
                                </button>
                            </div>
                        </form>
                    ) : (
                        <p className={styles.text}>{comment.text}</p>
                    )}
                    <div className={styles.actions}>
                        {depth < 2 && (
                            <button
                                className={styles.actionBtn}
                                onClick={() => setShowReply(!showReply)}
                                aria-label="Responder comentario"
                            >
                                <ArrowBendDownRight size={12} />
                                Responder
                            </button>
                        )}
                        {isOwner && !isEditing && (
                            <>
                                <button
                                    className={styles.actionBtn}
                                    onClick={() => setIsEditing(true)}
                                    aria-label="Editar comentario"
                                >
                                    <PencilSimple size={12} />
                                    Editar
                                </button>
                                <button
                                    className={`${styles.actionBtn} ${styles.deleteBtn}`}
                                    onClick={handleDeleteClick}
                                    aria-label="Eliminar comentario"
                                >
                                    <Trash size={12} />
                                    Eliminar
                                </button>
                            </>
                        )}
                        {isLogged && !isOwner && (
                            <button
                                className={styles.actionBtn}
                                onClick={() => setShowReportDialog(true)}
                                aria-label="Reportar comentario"
                            >
                                <Flag size={12} />
                                Reportar
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {showReply && (
                <div className={styles.replyInput}>
                    <CommentInput
                        onSubmit={handleReply}
                        placeholder="Escribe una respuesta..."
                        autoFocus
                    />
                </div>
            )}

            {comment.replies && comment.replies.length > 0 && (
                <div className={styles.replies}>
                    {comment.replies.map((reply) => (
                        <CommentItem
                            key={reply.id}
                            comment={reply}
                            onReply={onReply}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
